import { cn } from "@win-reality/react-ui";

import {
  getPlayResultHelperText,
  PITCH_RESULT_OPTIONS,
} from "../../constants/tagOptions";
import { Sport } from "../../types/gameFull";
import { isPitchTypeClassified } from "../../utils/pitchTagging";
import { formatTagValue } from "../../utils/plateAppearanceFeed";
import { OptionButton } from "./OptionButton";
import { PitchResultTile } from "./PitchResultTile";
import { ResultsSection } from "./ResultsSection";
import { isSectionAvailable, SECTION_OPTIONS, TagKey } from "./sections";
import { CollapsibleTagSection, TagSection } from "./TagSection";
import { usePitchTagsForm } from "./usePitchTagsForm";

const BADGE_FIELDS: Record<TagKey, string> = {
  pitchType: "pitch-type",
  pitchResult: "pitch-result",
  contactType: "contact-type",
  playResult: "play-result",
};

type TagSectionsProps = {
  form: ReturnType<typeof usePitchTagsForm>;
  /**
   * Waiting on the pitch, or briefly on its hit data: only pitch type can be
   * marked, and the rest is grayed out
   */
  isLoading: boolean;
  sport: Sport;
};

/** The Pitch Details sections, one open at a time, then the results. */
export const TagSections = ({ form, isLoading, sport }: TagSectionsProps) => {
  const { tags, activeSection } = form;
  const sectionOptions = SECTION_OPTIONS[sport];
  const playResultHelperText = getPlayResultHelperText(tags.pitchResult);

  const collapsibleProps = (section: TagKey) => ({
    "data-testid": "pitch-tagging-section-panel",
    section,
    field: BADGE_FIELDS[section],
    isActive: activeSection === section,
    value: formatTagValue(section, tags[section], sport),
    rawValue: tags[section],
    isAutoDetected: form.isAutoDetected(section),
    isDisabled: !isSectionAvailable(section, tags, isLoading),
    onToggle: () => form.toggleSection(section),
  });

  // Pitch result has its own tiles
  const renderOptions = (
    section: Exclude<TagKey, "pitchResult">,
    columns: string,
  ) => {
    const isPitchType = section === "pitchType";
    const isContactType = section === "contactType";
    return (
      <div className={cn("grid gap-2", columns)}>
        {sectionOptions[section].map((option) => {
          const isSuggested =
            option.value === form.suggestions[section] &&
            !form.isOptionDisabled(section, option.value);
          return (
            <OptionButton
              key={option.value}
              data-testid={
                isPitchType
                  ? "pitch-tagging-pitch-type-option"
                  : isContactType
                    ? "pitch-tagging-contact-type-option"
                    : "pitch-tagging-play-result-option"
              }
              data-value={option.value}
              label={option.label}
              hotkey={option.hotkey}
              isSelected={tags[section] === option.value}
              isSuggested={isSuggested}
              isDisabled={form.isOptionDisabled(section, option.value)}
              onClick={() => form.select(section, option.value)}
            />
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-3">
      <CollapsibleTagSection
        label="Pitch Type"
        {...collapsibleProps("pitchType")}
        // The classifier's pick comes with the pitch, unless one was made or
        // the sport has none
        isLoading={
          isLoading && tags.pitchType === null && isPitchTypeClassified(sport)
        }
      >
        {renderOptions("pitchType", "grid-cols-4")}
      </CollapsibleTagSection>

      <CollapsibleTagSection
        label="Pitch Result"
        {...collapsibleProps("pitchResult")}
      >
        <div className="grid grid-cols-4 gap-2">
          {PITCH_RESULT_OPTIONS.map((option) => (
            <PitchResultTile
              key={option.value}
              data-testid="pitch-tagging-pitch-result-option"
              value={option.value}
              label={option.label}
              hotkey={option.hotkey}
              isSelected={tags.pitchResult === option.value}
              isSuggested={option.value === form.suggestions.pitchResult}
              onClick={() => form.select("pitchResult", option.value)}
            />
          ))}
        </div>
      </CollapsibleTagSection>

      <CollapsibleTagSection
        label="Contact Type"
        {...collapsibleProps("contactType")}
      >
        {renderOptions("contactType", "grid-cols-2")}
      </CollapsibleTagSection>

      <CollapsibleTagSection
        label="Outcome of the play"
        {...collapsibleProps("playResult")}
      >
        <div className="space-y-3">
          {playResultHelperText && (
            <div
              data-testid="pitch-tagging-play-result-hint-status"
              className="rounded-[4px] bg-tagging-hotkey px-3 py-2 text-xs text-tagging-muted dark:text-white"
            >
              {playResultHelperText}
            </div>
          )}
          {renderOptions("playResult", "grid-cols-3")}
        </div>
      </CollapsibleTagSection>

      <TagSection
        data-testid="pitch-tagging-section-panel"
        label="Results From This Play"
        section="results"
        isActive={activeSection === "results"}
        isDisabled={isLoading}
      >
        <ResultsSection
          runsScored={tags.runsScored}
          outsRecorded={tags.outsRecorded}
          baseRunners={tags.baseRunners}
          edited={form.resultsEdited}
          onRunsScoredChange={(value) => form.changeResult("runsScored", value)}
          onOutsRecordedChange={(value) =>
            form.changeResult("outsRecorded", value)
          }
          onBaseRunnersChange={(value) =>
            form.changeResult("baseRunners", value)
          }
        />
      </TagSection>
    </div>
  );
};
